import React from 'react';
import {
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  LoaderFunctionArgs,
} from 'react-router-dom';
import { useApiClient } from '@marageti/z4-lib';
import Agent from '../pages/agent';
import AgentProfile from '../pages/agent/profile';
import AgentTrips from '../pages/agent/trips';
import Login from '../pages/login';
import ResetSendLink from '../pages/reset/send-link';
import ResetSetPassword from '../pages/reset/set-password';
import Activate from '../pages/activate';
import Home from '../pages/home';
import Logout from '../pages/logout';
import NotFound from '../pages/404';
import Dummy from '../pages/dummy';
import AddPartner from '../pages/partner-add';
import Partner from '../pages/partner';
import Partners from '../pages/partners';
import PartnerPeople from '../pages/partner/people';
import PartnerInvoice from '../pages/partner/invoice';
import PartnerProfile from '../pages/partner/profile';
import PartnerReports from '../pages/partner/reports';
import PartnerReviews from '../pages/partner/reviews/reviews-container';
import PartnerTickets from '../pages/partner/tickets';
import PartnerTrips from '../pages/partner/trips';
import PartnerAvailability from '../pages/partner/settings/availability';
import PartnerCountries from '../pages/partner/settings/countries';
import PartnerSettingsLayout from '../pages/partner/settings-layout';
import PartnerSettingsHome from '../pages/partner/settings/home';
import PartnerNonCompete from '../pages/partner/settings/non-compete';
import PartnerAddNonCompete from '../pages/partner/settings/non-compete-edit';
import PartnerNotes from '../pages/partner/settings/notes';
import Settings from '../pages/settings';
import SettingsCountryDefaults from '../pages/settings/country-defaults';
import SettingsDestinations from '../pages/settings/destinations';
import SettingsProfile from '../pages/settings/profile';
import SettingsTemplates from '../pages/settings/templates';
import SettingsAddTemplate from '../pages/settings/templates/add';
import SettingsEditTemplate from '../pages/settings/templates/edit';
import SettingsUsers from '../pages/settings/users';
import SettingsUser from '../pages/settings/users/user-profile';
import SettingsAudit from '../pages/settings/audit';
import AuditEvents from '../pages/settings/audit/events';
import AuditVersions from '../pages/settings/audit/versions';
import AuditVersionDetail from '../pages/settings/audit/versions/version-detail';
import AuditVersionComparison from '../pages/settings/audit/versions/version-comparison';
import AuditSalesStage from '../pages/settings/audit/sales-stage';
import AuditPageViews from '../pages/settings/audit/page-views';
import MergeTravelers from '../pages/tools/merge-travelers/merge-travelers-container';
import Review from '../pages/review';
import ReviewEdit from '../pages/review/edit-review';
import Reviews from '../pages/reviews';
import Tickets from '../pages/tickets';
import TicketDetailPage from '../pages/ticket';
import ProtectedLayout from './wrappers/layout-wrapper';
import ProtectedRoutes from './wrappers/protected-wrapper';
import ProtectedStore from './wrappers/store-wrapper';
import { useAuthStore } from '../store';
import useTravelersRoutes from './travelers';
import useTripMatchesRoutes from './trip-matches';
import useTripRequestsRoutes from './trip-requests';
import useSalesRoutes from './sales';
import useFullPageRoutes from './full-page';
import RedirectNavigator from './wrappers/redirect-wrapper';
import RouterErrorBoundary from '../components/error-boundary/ErrorBoundaryRouter';
import D7TripRedirectWrapper from './wrappers/d7-trip-redirect-wrapper';

const Router = () => {
  const { isAuthenticated } = useAuthStore();
  const apiClient = useApiClient();
  const travelersRoutes = useTravelersRoutes();
  const tripMatchesRoutes = useTripMatchesRoutes();
  const tripRequestsRoutes = useTripRequestsRoutes();
  const salesRoutes = useSalesRoutes();
  const fullPageRoutes = useFullPageRoutes();

  const tokenExistsLoader = async ({ request }: LoaderFunctionArgs): Promise<boolean> => {
    const { searchParams } = new URL(request.url);
    const email = searchParams.get('email') || '';
    const token = searchParams.get('token') || '';
    return apiClient.authClient.resetTokenExists(email, token).catch(() => false);
  };

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<RedirectNavigator />} ErrorBoundary={RouterErrorBoundary}>
        <Route path="/login" element={isAuthenticated ? <Navigate to={`/${window.location.search}`} replace /> : <Login />} />
        <Route path="logout" element={<Logout />} />
        <Route path="/reset" element={<ResetSendLink />} />
        <Route path="/reset/password" element={<ResetSetPassword />} loader={tokenExistsLoader} />
        <Route path="/activate" element={<Activate />} />
        <Route path="/" element={<ProtectedRoutes />}>
          <Route element={<D7TripRedirectWrapper />}>
            <Route element={<ProtectedStore />}>
              <Route element={<ProtectedLayout />}>
                <Route index element={<Home />} />
                <Route path="partners">
                  <Route index element={<Partners />} />
                  <Route path="add-partner" element={<AddPartner />} />
                </Route>
                <Route path="partners/:partnerId" element={<Partner />}>
                  <Route index element={<PartnerProfile />} handle={{ tab: '/' }} />
                  <Route path="trip-matches" element={<PartnerTrips />} handle={{ tab: '/trip-matches' }} />
                  <Route path="profile" element={<PartnerProfile />} handle={{ tab: '/profile' }} />
                  <Route path="reviews" element={<PartnerReviews />} handle={{ tab: '/reviews' }} />
                  <Route path="tickets" element={<PartnerTickets />} handle={{ tab: '/tickets' }} />
                  <Route path="people" element={<PartnerPeople />} handle={{ tab: '/people' }} />
                  <Route path="invoice" element={<PartnerInvoice />} handle={{ tab: '/invoice' }} />
                  <Route path="reports" element={<PartnerReports />} handle={{ tab: '/reports' }} />
                  <Route path="settings" element={<PartnerSettingsHome />} handle={{ tab: '/settings' }} />
                  <Route path="settings" element={<PartnerSettingsLayout />}>
                    <Route path="availability" element={<PartnerAvailability />} handle={{ tab: '/settings' }} />
                    <Route path="countries" element={<PartnerCountries />} handle={{ tab: '/settings' }} />
                    <Route path="non-compete" element={<PartnerNonCompete />} handle={{ tab: '/settings' }} />
                    <Route path="notes" element={<PartnerNotes />} handle={{ tab: '/settings' }} />
                  </Route>
                </Route>
                <Route path="partners/:partnerId/settings/non-compete/add" element={<PartnerAddNonCompete />} />
                <Route path="agents/:agentId" element={<Agent />}>
                  <Route index element={<AgentProfile />} handle={{ tab: '/' }} />
                  <Route path="trip-matches" element={<AgentTrips />} handle={{ tab: '/trip-matches' }} />
                  <Route path="profile" element={<AgentProfile />} handle={{ tab: '/profile' }} />
                </Route>
                {travelersRoutes}
                {tripMatchesRoutes}
                {tripRequestsRoutes}
                {salesRoutes}
                <Route path="tickets" element={<Tickets />} />
                <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
                <Route path="reviews">
                  <Route index element={<Reviews />} />
                  <Route path=":reviewId" element={<Review />} />
                  <Route path=":reviewId/edit" element={<ReviewEdit />} />
                </Route>
                <Route path="settings" element={<Settings />}>
                  <Route index element={<SettingsProfile />} handle={{ tab: '/' }} />
                  <Route path="country-defaults" element={<SettingsCountryDefaults />} handle={{ tab: '/country-defaults' }} />
                  <Route path="destinations" element={<SettingsDestinations />} handle={{ tab: '/destinations' }} />
                  <Route path="users" element={<SettingsUsers />} handle={{ tab: '/users' }} />
                  <Route path="templates" element={<SettingsTemplates />} handle={{ tab: '/templates' }} />
                  <Route path="users/:userId" element={<SettingsUser />} handle={{ tab: '/users' }} />
                  <Route path="audit" element={<SettingsAudit />} handle={{ tab: '/audit' }}>
                    <Route path="events" element={<AuditEvents />} />
                    <Route path="versions" element={<AuditVersions />} />
                    <Route path="versions/compare/:entityType/:id" element={<AuditVersionComparison />} />
                    <Route path="versions/:entityType/:id" element={<AuditVersionDetail />} />
                    <Route path="sales-stage" element={<AuditSalesStage />} />
                    <Route path="views" element={<AuditPageViews />} />
                  </Route>
                </Route>
                <Route path="/tools/merge-travelers" element={<MergeTravelers />} />
                <Route path="settings/templates/add" element={<SettingsAddTemplate />} />
                <Route path="settings/templates/:templateId" element={<SettingsEditTemplate />} />
                <Route path="dummy" element={<Dummy />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              {fullPageRoutes}
            </Route>
          </Route>
        </Route>
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
};

export default Router;
